import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Backdrop, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import HomeBannerForm from "../../component/admin/formComponents/HomeBannerForm";
import { getHomeBanner } from "../../api/getHomeBanner";
import { fetchHomeBannerCategories } from "../../action/getHomeBannerCategory";
import { fetchHomeBannersData } from "../../action/homeBannersActions";

export default function EditHomeBanner() {
  const token = useSelector((state) => state.auth.token);
  const bannersData = useSelector((state) => state.homeBanners);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (token) {
      dispatch(fetchHomeBannerCategories(token));
      dispatch(fetchHomeBannersData(token, 1));
    }
  }, [dispatch, token]);

  const usedCategoryIds = useMemo(() => {
    const rows = bannersData?.data?.data?.rows || bannersData?.data?.rows || [];
    return rows
      .map((b) => Number(b.category))
      .filter((cid) => !Number.isNaN(cid));
  }, [bannersData]);

  const formik = useFormik({
    initialValues: {
      category: "",
      image: "",
      banner_name: "",
      sort_order: 0,
    },
    validationSchema: Yup.object().shape({
      category: Yup.number("Please enter integer value")
        .required("Category is Required")
        .typeError("Category is Required"),
      image: Yup.mixed().required("Banner Image is required"),
      banner_name: Yup.string().max(120, "Title is too long"),
      sort_order: Yup.number()
        .typeError("Sort order must be a number")
        .min(0, "Sort order cannot be negative"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const data = new FormData();
      data.append("category", values.category);
      data.append("image", values.image);
      data.append("banner_name", values.banner_name.trim());
      data.append("sort_order", values.sort_order);
      data.append("id", id);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/editHomeBanner`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          toast.success(response.data.message);
          setTimeout(() => {
            navigate("/admin/dashboard/home-banners");
            setLoading(false);
          }, 1000);
        } else {
          toast.error(response.data.message);
          setLoading(false);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "error !!");
        setLoading(false);
      }
    },
  });

  const fetchBannerData = async (bannerId, authToken) => {
    try {
      const data = await getHomeBanner(bannerId, authToken);
      if (data.success) {
        formik.setValues({
          category: data.data.category,
          image: data.data.banner_img,
          banner_name: data.data.banner_name || "",
          sort_order: data.data.sort_order ?? 0,
        });
        setLoading(false);
      } else {
        toast.error(data.message);
        setLoading(false);
      }
    } catch (e) {
      toast.error("Error while fetching home banner data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBannerData(id, token);
    } else {
      setLoading(false);
      toast.error("Banner ID is missing");
      navigate("/admin/dashboard/home-banners");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <ToastContainer />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress sx={{ color: "#ef364e" }} />
      </Backdrop>
      <Box className="free_votes_page">
        <Box className="page_title">
          <Typography variant="h1" component="h1">
            Update Home Banner
          </Typography>
        </Box>

        <HomeBannerForm formik={formik} edited={true} />
      </Box>
    </React.Fragment>
  );
}
